import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return "00:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}ч ${m}м ${s}с`;
  return `${m}м ${s}с`;
};

const formatDurationVerbose = (seconds) => {
  if (!seconds || seconds <= 0) return "0 мин 00 сек";
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m} мин ${String(s).padStart(2, "0")} сек`;
};

const getStatusLabel = (code) => {
  switch (code) {
    case 0: return "В движении по маршруту";
    case 1: return "Контейнерная площадка";
    case 2: return "Манипуляции с баком";
    case 4: return "Нарушение ТКО / КГО";
    case 6: return "Оператор";
    case 7: return "Погрузка / Опрокидыватель";
    case 10: return "Простой на маршруте";
    case -1: return "Нет данных / Связь потеряна";
    default: return "Неизвестно";
  }
};

export const handleExportGarbageExcel = async ({
  analyticsData,
  startDate,
  endDate,
  device,
  setExportingExcel,
  toast
}) => {
  if (!analyticsData) {
    toast.warn("Сначала загрузите аналитику");
    return;
  }
  setExportingExcel(true);

  try {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "Neimarker AI";
    workbook.created = new Date();

    const kpis = analyticsData.kpis || {};
    const sites = analyticsData.sites_summary || [];

    // =========================================================================
    // ЛИСТ 1: СВОДНАЯ СТАТИСТИКА + СВОДНЫЕ KPI ЭКИПАЖА ЗА СМЕНУ
    // =========================================================================
    const sheetSummary = workbook.addWorksheet("Сводка по маршруту", { views: [{ showGridLines: false }] });
    sheetSummary.columns = [
      { key: "param", width: 42 },
      { key: "val", width: 26 },
      { key: "norm", width: 22 },
      { key: "status", width: 38 },
    ];

    const titleRow = sheetSummary.addRow({ param: "=== ИНФОРМАЦИЯ ОБ ОТЧЕТЕ ===" });
    titleRow.font = { bold: true, size: 12 };
    sheetSummary.addRow({ param: "Период:", val: `${dayjs(startDate).format("DD.MM.YYYY HH:mm")} - ${dayjs(endDate).format("DD.MM.YYYY HH:mm")}` });
    sheetSummary.addRow({ param: "Техника (Мусоровоз):", val: device?.name || "" });
    sheetSummary.addRow({});

    // --- БЛОК: СВОДНЫЕ KPI ЭКИПАЖА ЗА СМЕНУ ---
    if (sites.length > 0) {
      const totalSites = sites.length;
      let totalBinsFound = 0;
      let totalBinsEmptied = 0;
      const unemptiedLabels = [];
      let totalDurationSec = 0;
      let totalActiveLoadSec = 0;

      sites.forEach((s, idx) => {
        const count = Number(s.containers_count) || 0;
        const unemptied = s.has_unemptied_containers ? (Number(s.unemptied_bins_count) || 1) : 0;
        const emptied = Math.max(0, count - unemptied);
        totalBinsFound += count;
        totalBinsEmptied += emptied;
        if (s.has_unemptied_containers) unemptiedLabels.push(`КП #${idx + 1}`);

        const dur = Number(s.duration_sec) || 0;
        totalDurationSec += dur;
        const act = s.active_loading_sec !== undefined ? Number(s.active_loading_sec) : Math.min(dur * 0.85, count * 21.5);
        totalActiveLoadSec += act;
      });

      const totalUnemptied = totalBinsFound - totalBinsEmptied;
      const avgSiteSec = totalDurationSec / totalSites;
      const diffSiteSec = Math.round(avgSiteSec - 240);
      const avgBinCycleSec = totalBinsEmptied > 0 ? totalActiveLoadSec / totalBinsEmptied : 0;
      const activeLoadPct = totalDurationSec > 0 ? Math.round((totalActiveLoadSec / totalDurationSec) * 100) : 0;
      const idlePct = totalDurationSec > 0 ? Math.max(0, 100 - activeLoadPct) : 0;
      const overTimedCount = sites.filter((s) => Number(s.duration_sec) > 240).length;
      const onScheduleCount = Math.max(0, totalSites - overTimedCount);
      const onSchedulePct = Math.round((onScheduleCount / totalSites) * 100);

      const crewTitle = sheetSummary.addRow({ param: "=== СВОДНЫЕ KPI ЭКИПАЖА ЗА СМЕНУ ===" });
      crewTitle.font = { bold: true, size: 12 };

      const crewHeader = sheetSummary.addRow({
        param: "Метрика",
        val: "Значение по факту",
        norm: "Норматив / План",
        status: "Отклонение / Статус",
      });
      crewHeader.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
      crewHeader.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2EB85C" } };
      crewHeader.alignment = { vertical: "middle", horizontal: "center" };
      crewHeader.height = 26;

      const crewRows = [
        {
          param: "Выгружено баков",
          val: `${totalBinsEmptied} из ${totalBinsFound} шт`,
          norm: "100%",
          status: totalUnemptied > 0 ? `⚠️ ${totalUnemptied} недогруз (${unemptiedLabels.join(", ")})` : "🟢 100% выполнено",
        },
        {
          param: "Ср. время на 1 площадку",
          val: formatDurationVerbose(avgSiteSec),
          norm: "4 мин 00 сек",
          status: diffSiteSec > 0 ? `🔴 +${formatDuration(diffSiteSec)} (влияние навалов)` : "🟢 В норме",
        },
        {
          param: "Ср. скорость на 1 бак (цикл)",
          val: `${avgBinCycleSec.toFixed(1)} сек / бак`,
          norm: "18–25 сек",
          status: avgBinCycleSec > 25 ? `🔴 +${(avgBinCycleSec - 25).toFixed(1)}с` : "🟢 В норме",
        },
        {
          param: "Чистая погрузка vs Простои",
          val: `${activeLoadPct}% / ${idlePct}%`,
          norm: "80% / 20%",
          status: idlePct > 20 ? "🟡 Много задержек на маневры" : "🟢 В норме",
        },
        {
          param: "КП в графике (без аномалий)",
          val: `${onScheduleCount} из ${totalSites} (${onSchedulePct}%)`,
          norm: "> 85%",
          status: onSchedulePct < 85 ? `🔴 ${overTimedCount} КП с превышением тайминга` : "🟢 В графике",
        },
      ];

      crewRows.forEach((cr) => {
        const r = sheetSummary.addRow(cr);
        r.height = 22;
        r.getCell(1).font = { bold: true };
        r.getCell(2).font = { bold: true };
        r.getCell(2).alignment = { horizontal: "center", vertical: "middle" };
        r.getCell(3).alignment = { horizontal: "center", vertical: "middle" };
        r.getCell(4).alignment = { horizontal: "left", vertical: "middle" };
        for (let c = 1; c <= 4; c++) {
          r.getCell(c).border = { bottom: { style: "thin", color: { argb: "FFCCCCCC" } } };
        }
      });

      sheetSummary.addRow({});
    }

    const headerRow = sheetSummary.addRow({ param: "Параметр", val: "Значение" });
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 12 };
    headerRow.getCell(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4472C4" } };
    headerRow.getCell(2).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4472C4" } };
    headerRow.alignment = { vertical: "middle", horizontal: "center" };
    headerRow.height = 30;

    const summaryData = [
      { param: "=== ОСНОВНЫЕ ПОКАЗАТЕЛИ (KPI) ===", val: "", isSubHeader: true },
      { param: "Обслужено контейнерных площадок (КП)", val: kpis.total_sites_visited || 0 },
      { param: "Опорожнено контейнеров (шт)", val: kpis.total_containers_emptied || 0 },
      { param: "Пробег по GPS (км)", val: kpis.total_mileage_km || 0 },
      { param: "", val: "" },
      { param: "=== ФИКСАЦИЯ НАРУШЕНИЙ ===", val: "", isSubHeader: true },
      { param: "Площадок с нарушениями (всего)", val: kpis.violations_count || 0 },
      { param: "  - Навалы ТКО (вне бака)", val: kpis.overflow_sites_count || 0 },
      { param: "  - Крупногабаритные отходы (КГО)", val: kpis.bulky_waste_sites_count || 0 },
      { param: "  - Дефектные контейнеры", val: kpis.defective_containers_count || 0 },
      { param: "  - Недогруз (остались полные баки)", val: kpis.unemptied_containers_count || 0 },
      { param: "", val: "" },
      { param: "=== БАЛАНС ВРЕМЕНИ ===", val: "", isSubHeader: true },
      { param: "Время в движении", val: formatDuration(kpis.time_on_route_sec) },
      { param: "Время работы на площадках (КП)", val: formatDuration(kpis.time_at_sites_sec) },
      { param: "Время простоев на маршруте", val: formatDuration(kpis.time_downtime_sec) },
    ];

    summaryData.forEach((item) => {
      const row = sheetSummary.addRow({ param: item.param, val: item.val });
      if (item.param === "") {
        row.height = 10;
      } else {
        row.height = 20;
        row.getCell(1).alignment = { vertical: "middle", horizontal: "left" };
        row.getCell(2).alignment = { vertical: "middle", horizontal: "center" };
        
        if (item.isSubHeader) {
          row.getCell(1).font = { bold: true, color: { argb: "FF000000" } };
          row.getCell(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFEFEF" } };
        } else {
          row.getCell(1).border = { bottom: { style: "thin", color: { argb: "FFCCCCCC" } } };
          row.getCell(2).border = { bottom: { style: "thin", color: { argb: "FFCCCCCC" } } };
        }
      }
    });

    // =========================================================================
    // ЛИСТ 2: ДЕТАЛИЗАЦИЯ ПО ПЛОЩАДКАМ
    // =========================================================================
    const sheetSites = workbook.addWorksheet("Журнал обслуживания КП");
    sheetSites.columns = [
      { header: "№", key: "id", width: 5 },
      { header: "Дата", key: "date", width: 12 },
      { header: "Заезд", key: "start", width: 12 },
      { header: "Выезд", key: "end", width: 12 },
      { header: "Длительность", key: "dur", width: 15 },
      { header: "Баков", key: "bins", width: 10 },
      { header: "Нарушения ИИ", key: "violations", width: 45 },
      { header: "Адрес", key: "address", width: 50 },
      { header: "Координаты", key: "gps", width: 25 },
    ];

    const sitesHeader = sheetSites.getRow(1);
    sitesHeader.font = { bold: true, color: { argb: "FFFFFFFF" } };
    sitesHeader.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4472C4" } };
    sitesHeader.alignment = { horizontal: "center", vertical: "middle" };
    sitesHeader.height = 25;

    sites.forEach((site, idx) => {
      const violations = [];
      if (site.has_unemptied_containers) violations.push("Недогруз (полные баки)");
      if (site.has_overflow) violations.push("Навал ТКО");
      if (site.has_bulky_waste) violations.push("КГО");
      if (site.has_container_defect) violations.push("Дефект бака");
      
      const vStr = violations.length > 0 ? violations.join(", ") : "Чисто / Без нарушений";

      const row = sheetSites.addRow({
        id: idx + 1,
        date: dayjs(site.start).format("DD.MM.YYYY"),
        start: dayjs(site.start).format("HH:mm:ss"),
        end: dayjs(site.end).format("HH:mm:ss"),
        dur: formatDuration(site.duration_sec),
        bins: site.containers_count,
        violations: vStr,
        address: site.address || "Не определен",
        gps: (site.latitude && site.longitude) ? `${site.latitude.toFixed(5)}, ${site.longitude.toFixed(5)}` : "-"
      });

      row.alignment = { vertical: "middle", horizontal: "center" };
      row.getCell(7).alignment = { vertical: "middle", horizontal: "left", wrapText: true };
      row.getCell(8).alignment = { vertical: "middle", horizontal: "left", wrapText: true };

      if (violations.length > 0) {
        row.getCell(7).font = { color: { argb: "FFC00000" }, bold: true };
      } else {
        row.getCell(7).font = { color: { argb: "FF00B050" } };
      }
    });

    // =========================================================================
    // ЛИСТ 3: ХРОНОЛОГИЯ (ТАЙМЛАЙН)
    // =========================================================================
    const sheetTimeline = workbook.addWorksheet("Хронология маршрута");
    sheetTimeline.columns = [
      { header: "Начало", key: "start", width: 20 },
      { header: "Конец", key: "end", width: 20 },
      { header: "Длительность", key: "dur", width: 15 },
      { header: "Статус", key: "status", width: 40 },
    ];

    const tlHeader = sheetTimeline.getRow(1);
    tlHeader.font = { bold: true, color: { argb: "FFFFFFFF" } };
    tlHeader.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF5A5A5A" } };
    tlHeader.alignment = { horizontal: "center", vertical: "middle" };

    const timeline = analyticsData.timeline || [];
    timeline.forEach(item => {
      const row = sheetTimeline.addRow({
        start: dayjs(item.start).format("DD.MM.YYYY HH:mm:ss"),
        end: dayjs(item.end).format("DD.MM.YYYY HH:mm:ss"),
        dur: formatDuration(item.duration_sec),
        status: getStatusLabel(item.status_code)
      });
      
      row.alignment = { vertical: "middle", horizontal: "center" };
      row.getCell(4).alignment = { vertical: "middle", horizontal: "left" };

      if (item.status_code === 10) row.getCell(4).font = { color: { argb: "FFC00000" } };
      if (item.status_code === 1) row.getCell(4).font = { color: { argb: "FF0070C0" }, bold: true };
    });

    // =========================================================================
    // СОХРАНЕНИЕ ФАЙЛА
    // =========================================================================
    const buffer = await workbook.xlsx.writeBuffer();
    const filename = `${device?.name || "GarbageTruck"}_Analytics_${dayjs().format("YYYY-MM-DD")}.xlsx`;
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, filename);

    toast.success("Отчет Excel успешно скачан!");
  } catch (error) {
    console.error("Ошибка генерации Excel:", error);
    toast.error("Ошибка при формировании отчета Excel.");
  } finally {
    setExportingExcel(false);
  }
};